import Tooltip from 'components/Tooltip';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useContext } from 'react';
import { ViewContext } from 'components/lib';

export default function Notes() {
  const { id } = useParams();
  const context = useContext(ViewContext);

  const [note, setNote] = useState('');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/company/${id}`);
      if (response.data.success && response.data.data) {
        setNotes(Array.isArray(response.data.data.note) ? response.data.data.note : []);
      }
      setLoading(false);
    } catch (err) {
      context.notification.show(
        error || 'Failed to load Notes',
        'error',
        false,
        'toast',
        'x-circle'
      );

      setError("Failed to load notes. Please try again.");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchNotes();
    } else {
      setLoading(false);
    }
  }, [id]);

  const handleSaveNote = async () => {
    if (!note.trim()) return;

    try {
      setLoading(true);
      const response = await axios.put(`/api/company/${id}`, {
        note: note
      });

      if (response.data.success && response.data.data) {
        setNotes(Array.isArray(response.data.data.note) ? response.data.data.note : []);
        setNote('');
      }
      setLoading(false);
    } catch (err) {
      context.notification.show(
        error || 'Failed to Save Note',
        'error',
        false,
        'toast',
        'x-circle'
      );
      setError("Failed to save note. Please try again.");
      setLoading(false);
    } finally {
      fetchNotes();
    }
  };

  const handleEditNote = async (index, updatedText) => {
    try {
      setLoading(true);

      const updatedNotes = [...notes];
      updatedNotes[index] = updatedText;

      const response = await axios.put(`/api/company/${id}`, {
        note: updatedNotes
      });

      if (response.data.success && response.data.data) {
        setNotes(Array.isArray(response.data.data.note) ? response.data.data.note : []);
        fetchNotes();
      }

      setEditing(null);
      setLoading(false);
    } catch (err) {
      error || context.notification.show(
        'Failed to Save Note',
        'error',
        false,
        'toast',
        'x-circle'
      );
      setError("Failed to update note. Please try again.");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mt-4">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mt-4">
      <Tooltip
        text="Add a custom note that will be attached on the company profile. You can edit or delete this note later."
        position="right"
      >
        <h2 className="text-2xl font-bold mb-4">Notes</h2>
      </Tooltip>

      {/* Note input section */}
      <div className="mb-6">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full border rounded-md p-2 min-h-[100px]"
          rows={5}
          style={{ resize: 'vertical' }}
          maxLength={1500}
          placeholder="Write your note here..."
        />
        <div className="text-sm text-gray-500 mb-2 text-right">
          {note.length} / 1500 characters
        </div>

        <button
          onClick={handleSaveNote}
          disabled={!note.trim() || loading}
          className={`px-5 py-2.5 bg-red-600 text-white text-lg rounded font-semibold ${!note.trim() || loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
        >
          Add Note
        </button>
      </div>

      {/* Notes list */}
      {notes.length > 0 ? (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold">Saved Notes</h3>
          {notes.map((noteText, index) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg shadow border">
              {editing === index ? (
                <div>
                  <textarea
                    value={noteText}
                    onChange={(e) => {
                      const updatedNotes = [...notes];
                      updatedNotes[index] = e.target.value;
                      setNotes(updatedNotes);
                    }}
                    className="w-full border rounded-md p-2 mb-2"
                    rows={4}
                    maxLength={1500}
                  />
                  <div className="text-sm text-gray-500 mb-2 text-right">
                    {noteText.length} / 1500 characters
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => setEditing(null)}
                      className="px-3 py-1 bg-gray-200 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleEditNote(index, noteText)}
                      className="px-3 py-1 bg-red-600 text-white rounded"
                      disabled={!noteText.trim()}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="whitespace-pre-wrap mb-4">{noteText}</div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => setEditing(index)}
                      className="px-4 py-2 bg-gray-200 rounded font-semibold"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">No notes yet. Add one above!</div>
      )
      }
    </div >
  );
}
